import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Link, type LinkProps } from 'react-router-dom';

import { cn } from '../../lib/cn';
import styles from './StatePage.module.css';

export type StateTone = 'neutral' | 'warning' | 'danger';

interface StatePageProps {
  code: string;
  title: string;
  body: string;
  icon: LucideIcon;
  tone?: StateTone;
  primary: ReactNode;
  secondary?: ReactNode;
  details?: string | undefined;
}

export function StatePage({
  code,
  title,
  body,
  icon: Icon,
  tone = 'neutral',
  primary,
  secondary,
  details,
}: StatePageProps) {
  return (
    <div className={styles.wrap}>
      <div className={styles.card} data-tone={tone}>
        <div className={styles.iconWrap} aria-hidden="true">
          <Icon size={36} className={styles.icon} />
        </div>
        <span className={styles.code} aria-hidden="true">
          {code}
        </span>
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.body}>{body}</p>
        {details ? (
          <details className={styles.details}>
            <summary>פרטים טכניים</summary>
            <pre className={styles.detailsPre}>{details}</pre>
          </details>
        ) : null}
        <div className={styles.actions}>
          {primary}
          {secondary}
        </div>
      </div>
    </div>
  );
}

type Variant = 'primary' | 'secondary';

function classFor(variant: Variant): string | undefined {
  return variant === 'primary' ? styles.btnPrimary : styles.btnSecondary;
}

interface ActionLinkProps extends LinkProps {
  variant?: Variant;
}

export function StateLink({ variant = 'primary', className, ...rest }: ActionLinkProps) {
  return <Link className={cn(classFor(variant), className)} {...rest} />;
}

interface ActionAnchorProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  variant?: Variant;
}

export function StateAnchor({ variant = 'secondary', className, ...rest }: ActionAnchorProps) {
  return <a className={cn(classFor(variant), className)} {...rest} />;
}

interface ActionButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
}

export function StateButton({ variant = 'secondary', className, type = 'button', ...rest }: ActionButtonProps) {
  return <button type={type} className={cn(classFor(variant), className)} {...rest} />;
}
